import { html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { msg } from '@lit/localize';
import { SsoElement } from './sso-base.js';

let uid = 0;

/**
 * `<sso-text-field>` — accessible label + input + error line (SSO-MOCKUPS.md / §5.4).
 *
 * - Renders a real `<label for>` + `<input>` so it is fully labelled for screen readers.
 * - `error` text is wired via `aria-describedby` and announced through `aria-live="polite"`.
 * - `compact` removes the reserved empty error line for toolbar/filter rows.
 * - Password fields get a show/hide toggle (the 👁 in the mockups).
 * - Exposes `.value` and mirrors changes back to the attribute so a wrapping `FormData`
 *   can read it: the page reads `field.value` directly (we are in a shadow root, so the
 *   inner input is not visible to the form's FormData — pages collect values via refs/value).
 */
@customElement('sso-text-field')
export class SsoTextField extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      :host {
        display: block;
      }
      .wrap {
        display: grid;
        gap: 0.35rem;
      }
      label {
        font-family: var(--sso-font-body-family);
        font-weight: 700;
        color: var(--sso-color-fg);
        font-size: 0.9rem;
      }
      .control {
        position: relative;
        display: flex;
        align-items: center;
      }
      input {
        font: inherit;
        width: 100%;
        min-height: var(--sso-touch-target);
        padding: 0.6rem 0.75rem;
        border: 1px solid var(--sso-color-border);
        border-radius: var(--sso-radius);
        background: var(--sso-color-surface);
        color: var(--sso-color-fg);
      }
      input:focus-visible {
        outline: none;
        border-color: var(--sso-color-brand);
        box-shadow: var(--sso-focus-ring);
      }
      :host([invalid]) input {
        border-color: var(--sso-color-danger);
      }
      .toggle {
        position: absolute;
        inset-inline-end: 0.25rem;
        background: none;
        border: none;
        cursor: pointer;
        color: var(--sso-color-muted);
        font-size: 1.1rem;
        min-width: var(--sso-touch-target);
        min-height: var(--sso-touch-target);
        border-radius: var(--sso-radius);
      }
      .toggle:focus-visible {
        outline: none;
        box-shadow: var(--sso-focus-ring);
      }
      .error {
        color: var(--sso-color-danger);
        font-size: 0.85rem;
        min-height: 1em;
      }
      :host([compact]) .error:empty {
        display: none;
      }
    `,
  ];

  @property() name = '';
  @property() label = '';
  @property() type: 'text' | 'password' | 'email' | 'tel' = 'text';
  @property() value = '';
  @property() autocomplete = '';
  @property() inputmode = '';
  @property() placeholder = '';
  @property() error = '';
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid = false;
  @property({ type: Boolean, reflect: true }) compact = false;

  @state() private revealed = false;
  @query('input') private input!: HTMLInputElement;

  private readonly fieldId = `tf-${++uid}`;

  /** Focus the inner input (used by pages for first-field focus). */
  override focus(): void {
    // Wait for the inner <input> to render — the parent page may call this from its own
    // firstUpdated(), before this child element has completed its first update.
    void this.updateComplete.then(() => this.input?.focus());
  }

  /**
   * Restore native Enter-to-submit. The real <input> lives in this shadow root, so it is not a
   * descendant of the page <form> and implicit submission cannot cross the boundary — mirror
   * `sso-button`'s approach and request submit on the enclosing form ourselves.
   */
  private onKeyDown(e: KeyboardEvent): void {
    if (e.key !== 'Enter' || e.isComposing) return;
    const form = this.closest('form');
    if (form) {
      e.preventDefault();
      form.requestSubmit();
    }
  }

  private onInput(e: Event): void {
    this.value = (e.target as HTMLInputElement).value;
    this.invalid = false;
    this.error = '';
    this.dispatchEvent(new CustomEvent('input-change', { detail: this.value }));
  }

  private toggleReveal(): void {
    this.revealed = !this.revealed;
  }

  override render() {
    const isPassword = this.type === 'password';
    const effectiveType = isPassword && this.revealed ? 'text' : this.type;
    return html`
      <div class="wrap">
        <label for=${this.fieldId}>${this.label}</label>
        <div class="control">
          <input
            id=${this.fieldId}
            name=${this.name}
            .value=${this.value}
            type=${effectiveType}
            autocomplete=${this.autocomplete || nothing}
            inputmode=${this.inputmode || nothing}
            placeholder=${this.placeholder || nothing}
            ?required=${this.required}
            ?disabled=${this.disabled}
            aria-invalid=${this.invalid ? 'true' : 'false'}
            aria-describedby=${this.error ? `${this.fieldId}-err` : nothing}
            @input=${this.onInput}
            @keydown=${this.onKeyDown} />
          ${isPassword
            ? html`<button
                type="button"
                class="toggle"
                @click=${this.toggleReveal}
                aria-pressed=${this.revealed ? 'true' : 'false'}
                aria-label=${this.revealed ? msg('Hide password') : msg('Show password')}>
                ${this.revealed ? '🙈' : '👁'}
              </button>`
            : nothing}
        </div>
        <div class="error" id=${`${this.fieldId}-err`} aria-live="polite">${this.error}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sso-text-field': SsoTextField;
  }
}
